import AdventerousImg from 'src/assets/icons/template-previews/adventurous.png';
import BoldImg from 'src/assets/icons/template-previews/bold.png';
import DecoratedImg from 'src/assets/icons/template-previews/decorated.png';
import ElegantImg from 'src/assets/icons/template-previews/elegant.png';
import MinimalistImg from 'src/assets/icons/template-previews/minimalist.png';
import RobustImg from 'src/assets/icons/template-previews/robust.png';
import RusticImg from 'src/assets/icons/template-previews/rustic.png';
import StrikingImg from 'src/assets/icons/template-previews/striking.png';
import type {
  Structure,
  WallStylePolicy,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';
import {
  EdgeOrientation,
  EdgePosition,
  PartitionGuideline,
  PartitionSide,
  WallDirection,
} from 'src/modules/designStudio/helpers/data/TemplateDesignConfig';

import { LABEL_IDS } from '../../constants/productCatalog';

import type { TemplatePaletteConfig } from './PaletteStyles';
import { ACCENTED_PALETTES, SIMPLE_PALETTES } from './PaletteStyles';

export type TemplateWithIcon = {
  style: Structure;
  img: string;
  palettes: TemplatePaletteConfig[];
};

const nonMasonryWalls = [
  LABEL_IDS.HORIZONTAL_SIDING_ID,
  LABEL_IDS.OTHER_SIDING_ID,
  LABEL_IDS.SHINGLED_SIDING_ID,
  LABEL_IDS.VERTICAL_SIDING_ID,
  LABEL_IDS.VINYL_ID,
  LABEL_IDS.WOOD_ID,
  LABEL_IDS.WRAP_ID,
  LABEL_IDS.TUDOR_ID,
  LABEL_IDS.UNKNOWN_ID,
  LABEL_IDS.STUCCO_ID,
];

const TEXTURE_IDS = {
  [LABEL_IDS.HORIZONTAL_SIDING_ID]: 623,
  [LABEL_IDS.VERTICAL_SIDING_ID]: 1267,
  [LABEL_IDS.SHINGLED_SIDING_ID]: 23,
};

const templateTrialBasePolicy: WallStylePolicy = {
  onLabels: nonMasonryWalls,
};

const templateTrialDecoratedFront: WallStylePolicy = {
  onLabels: nonMasonryWalls,
  onSides: [WallDirection.FRONT],
};

const templateTrialGableTopPolicy: WallStylePolicy = {
  ...templateTrialDecoratedFront,
  hasPartitions: [
    {
      usesConstraints: [
        {
          cutAlong: PartitionGuideline.GABLE_CUT,
          keepSide: PartitionSide.ABOVE,
        },
      ],
    },
  ],
};

const basicDoorStyle = {
  name: 'door',
  policy: {
    onLabels: [101, 102],
    isFacing: ['front'],
  },
};

const garageDoorStyle = {
  name: 'garage',
  policy: {
    onLabels: [103],
  },
};

const orderedPalettes = (
  n: number,
  masterList: TemplatePaletteConfig[],
): TemplatePaletteConfig[] => {
  const palettes = [...masterList];
  palettes.splice(0, 0, ...palettes.splice(n, 1));
  return palettes;
};

const ALL_PALETTES = [...SIMPLE_PALETTES, ...ACCENTED_PALETTES];

const templateTrial1: TemplateWithIcon = {
  style: {
    designName: 'Minimalist',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/minimalist/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/minimalist/garage.glb',
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#6f7a7d',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.2032,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 556,
        },
      },
    ],
  },
  img: MinimalistImg,
  palettes: orderedPalettes(3, SIMPLE_PALETTES),
};

const templateTrial2: TemplateWithIcon = {
  img: ElegantImg,
  style: {
    designName: 'Elegant',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/elegant/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/elegant/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 568,
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#a4a192',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#c8bfb2',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#c8bfb2',
                textureId: 31,
              },
            },
          },
        ],
      },
      {
        name: 'accent',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#747771',
        },
        trims: [
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_BOTTOM],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#c8bfb2',
                textureId: 31,
              },
            },
          },
        ],
        policy: { ...templateTrialGableTopPolicy },
      },
    ],
  },
  palettes: orderedPalettes(10, ALL_PALETTES),
};

const templateTrial3: TemplateWithIcon = {
  img: RobustImg,
  style: {
    designName: 'Robust',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/robust/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/robust/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 564,
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#b2afac',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.2032,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
      },
      {
        name: 'accent',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.SHINGLED_SIDING_ID],
          color: '#b2afac',
        },
        policy: templateTrialGableTopPolicy,
      },
    ],
  },
  palettes: orderedPalettes(4, ALL_PALETTES),
};

const templateTrial4: TemplateWithIcon = {
  img: DecoratedImg,
  style: {
    designName: 'Decorated',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/decorated/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/decorated/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 565,
          // color: '#636766',
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#e0d5be',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              onOrientations: [
                EdgeOrientation.HORIZONTAL,
                EdgeOrientation.VERTICAL,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
      },
      {
        name: 'fronts',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#e0d5be',
        },
        trims: [
          {
            policy: {
              onOrientations: [
                EdgeOrientation.HORIZONTAL,
                EdgeOrientation.VERTICAL,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
        policy: templateTrialDecoratedFront,
      },
    ],
  },
  palettes: orderedPalettes(8, ALL_PALETTES),
};

const templateTrial5: TemplateWithIcon = {
  img: RusticImg,
  style: {
    designName: 'Rustic',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/rustic/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/rustic/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 569,
          color: '#6f777e',
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#b4b7b3',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.VERTICAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
      },
      {
        name: 'fronts',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.SHINGLED_SIDING_ID],
          color: '#b4b7b3',
        },
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
        policy: templateTrialDecoratedFront,
      },
    ],
  },
  palettes: orderedPalettes(7, ALL_PALETTES),
};

const templateTrial6: TemplateWithIcon = {
  img: StrikingImg,
  style: {
    designName: 'Striking',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/striking/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/striking/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 556,
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#e2e3dd',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              onOrientations: [
                EdgeOrientation.HORIZONTAL,
                EdgeOrientation.SLOPED,
              ],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#e2e3dd',
                textureId: 31,
              },
            },
          },
        ],
      },
    ],
  },
  palettes: orderedPalettes(0, SIMPLE_PALETTES),
};

const templateTrial7: TemplateWithIcon = {
  img: BoldImg,
  style: {
    designName: 'Bold',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/bold/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/bold/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 556,
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.SHINGLED_SIDING_ID],
          color: '#494e4b',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.INTERIOR_TOP,
                EdgePosition.INTERIOR_BOTTOM,
                EdgePosition.INTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#494e4b',
                textureId: 31,
              },
            },
          },
        ],
      },
    ],
  },
  palettes: orderedPalettes(2, SIMPLE_PALETTES),
};

const templateTrial8: TemplateWithIcon = {
  img: AdventerousImg,
  style: {
    designName: 'Adventurous',
    doorStyles: [
      {
        ...basicDoorStyle,
        appearance: {
          url: ' https://machete-terrain.hover.to/models/door/adventurous/entry.glb',
        },
      },
      {
        ...garageDoorStyle,
        appearance: {
          url: 'https://machete-terrain.hover.to/models/door/adventurous/garage.glb',
        },
      },
    ],
    roofStyles: [
      {
        policy: {},
        appearance: {
          textureId: 556,
        },
      },
    ],
    wallStyles: [
      {
        name: 'base',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.HORIZONTAL_SIDING_ID],
          color: '#e2e3dd',
        },
        policy: templateTrialBasePolicy,
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#494e4b',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              onOrientations: [EdgeOrientation.HORIZONTAL],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#494e4b',
                textureId: 31,
              },
            },
          },
        ],
      },
      {
        name: 'fronts',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#494e4b',
        },
        trims: [
          {
            policy: {},
            appearance: {
              width: 0.1016,
              texture: {
                color: '#494e4b',
                textureId: 31,
              },
            },
          },
          {
            policy: {
              withinFaces: [EdgePosition.EXTERIOR_SIDE],
            },
            appearance: {
              width: 0.1524,
              texture: {
                color: '#494e4b',
                textureId: 31,
              },
            },
          },
        ],
        policy: templateTrialDecoratedFront,
      },
      {
        name: 'tops',
        appearance: {
          textureId: TEXTURE_IDS[LABEL_IDS.VERTICAL_SIDING_ID],
          color: '#e2e3dd',
        },
        trims: [
          {
            policy: {
              withinFaces: [
                EdgePosition.EXTERIOR_TOP,
                EdgePosition.EXTERIOR_SIDE,
              ],
            },
            appearance: {
              width: 0.1016,
              texture: {
                color: '#494e4b',
                textureId: 31,
              },
            },
          },
        ],
        policy: templateTrialGableTopPolicy,
      },
    ],
  },
  palettes: orderedPalettes(1, ACCENTED_PALETTES),
};

export const TEMPLATE_STYLES: TemplateWithIcon[] = [
  templateTrial1,
  templateTrial2,
  templateTrial3,
  templateTrial4,
  templateTrial5,
  templateTrial6,
  templateTrial7,
  templateTrial8,
];
